<template>
    <div class="insurance">
        <section class="section page-wrapper insurance__section">
            <div class="section__content">
                <h1 class="section__heading section__heading--centered">
                    {{ $t('pages.insurance.heading') }}
                </h1>

                <div class="section__part">
                    <p>{{ $t('pages.insurance.omocom_intro_1') }}</p>
                    <p>{{ $t('pages.insurance.omocom_intro_2') }}</p>
                </div>

                <div class="divider" />

                <div class="section__part">
                    <h3 class="section__subheading">{{ $t('pages.insurance.insurance_claim_subheading') }}</h3>
                    <p>{{ $t('pages.insurance.insurance_claim_intro') }}</p>
                    <ol class="section__ordered-list">
                        <i18n path="pages.insurance.insurance_claim_step_assistance" tag="li">
                            <template #newLine>
                                <br />
                            </template>
                            <template #phoneNumber>
                                <a :href="'tel:' + getRoadsideAssistancePhone()">{{ getRoadsideAssistancePhone() }}</a>
                            </template>
                            <template #finlandPhoneNumber>
                                <br />
                                <a :href="'tel:' + getRoadsideAssistancePhone('FI')" class="insurance__phone-number">
                                    {{ getRoadsideAssistancePhone('FI') }}</a
                                >
                            </template>
                            <template #norwayPhoneNumber>
                                <br />
                                <a :href="'tel:' + getRoadsideAssistancePhone('NO')" class="insurance__phone-number">
                                    {{ getRoadsideAssistancePhone('NO') }}</a
                                >
                            </template>
                            <template #swedenPhoneNumber>
                                <br />
                                <a :href="'tel:' + getRoadsideAssistancePhone('SE')" class="insurance__phone-number">
                                    {{ getRoadsideAssistancePhone('SE') }}
                                </a>
                            </template>
                            <template #denmarkPhoneNumber>
                                <br />
                                <a :href="'tel:' + getRoadsideAssistancePhone('DK')" class="insurance__phone-number">
                                    {{ getRoadsideAssistancePhone('DK') }}
                                </a>
                            </template>
                            <template #germanyPhoneNumber>
                                <br />
                                <a :href="'tel:' + getRoadsideAssistancePhone('SE')" class="insurance__phone-number">
                                    {{ getRoadsideAssistancePhone('SE') }}
                                </a>
                            </template>
                            <template #austriaPhoneNumber>
                                <br />
                                <a :href="'tel:' + getRoadsideAssistancePhone('SE')" class="insurance__phone-number">
                                    {{ getRoadsideAssistancePhone('SE') }}
                                </a>
                            </template>
                        </i18n>
                        <li>
                            {{ $t('pages.insurance.insurance_claim_step_create_claim') }}
                        </li>
                    </ol>
                    <p>
                        {{ $t('pages.insurance.insurance_claim_note') }}
                    </p>
                    <Notice>
                        <template #heading>
                            {{ $t('pages.insurance.insurance_collision_notice_heading') }}
                        </template>

                        <i18n path="pages.insurance.insurance_collision_notice_description" tag="p">
                            <template #link>
                                <a
                                    href="/european-accident-statement-form.pdf"
                                    download
                                    class="insurance__download-link"
                                >
                                    {{ $t('pages.insurance.insurance_collision_notice_link') }}
                                </a>
                            </template>
                        </i18n>
                    </Notice>
                </div>
                <div class="section__part insurance__documents">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.terms_heading') }}
                    </h3>
                    <p>{{ $t('pages.insurance.terms_desc') }}</p>
                    <!-- EN/DE/CH -->
                    <template v-if="!siteCountryCode || siteCountryCode === 'CH'">
                        <div v-for="code in regionCodes" :key="code" class="freeze-open">
                            <h3>
                                {{ $t(`pages.insurance.renting_a_vehicle_in_${code}`) }}
                            </h3>
                            <template v-if="code !== 'DE' && code !== 'AT'">
                                <p>
                                    <a :href="getInsuranceTermsUrl(code, true)" target="_blank">
                                        <svg-icon name="download" /><span>{{ $t('pages.insurance.terms_link') }}</span>
                                    </a>
                                </p>
                                <p>
                                    <a :href="getCancellationInsuranceTermsUrl(code, true)" target="_blank">
                                        <svg-icon name="download" /><span>{{
                                            $t('pages.insurance.cancellation_terms_link')
                                        }}</span>
                                    </a>
                                </p>
                            </template>
                            <p>
                                <a :href="getInsuranceProductInfoUrl(code, true)" target="_blank">
                                    <svg-icon name="download" />
                                    <span>{{ $t('pages.insurance.product_info_link') }}</span>
                                </a>
                            </p>
                        </div>
                    </template>
                    <!-- Dynamic for SE/NO/FI/DK -->
                    <TextExpander
                        v-for="(code, index) in regionCodes"
                        :key="index"
                        :freeze-open="index === 0 && !!siteCountryCode && siteCountryCode !== 'CH'"
                        :show-label="regionExpanderLabel(code)"
                        class="insurance__expander"
                        :class="{ 'freeze-open': index === 0 && !!siteCountryCode && siteCountryCode !== 'CH' }"
                    >
                        <template v-if="code !== 'DE' && code !== 'AT'">
                            <p>
                                <a :href="getInsuranceTermsUrl(code)" target="_blank">
                                    <svg-icon name="download" />
                                    <span>
                                        {{
                                            $t(
                                                'pages.insurance.terms_link',
                                                $i18n.locale === 'fr' ? 'fr' : code.toLowerCase()
                                            )
                                        }}
                                    </span>
                                </a>
                            </p>
                            <p>
                                <a :href="getCancellationInsuranceTermsUrl(code)" target="_blank">
                                    <svg-icon name="download" />
                                    <span>
                                        {{
                                            $t(
                                                'pages.insurance.cancellation_terms_link',
                                                $i18n.locale === 'fr' ? 'fr' : code.toLowerCase()
                                            )
                                        }}
                                    </span>
                                </a>
                            </p>
                        </template>
                        <p>
                            <a :href="getInsuranceProductInfoUrl(code)" target="_blank">
                                <svg-icon name="download" />
                                <span>
                                    {{
                                        $t(
                                            'pages.insurance.product_info_link',
                                            $i18n.locale === 'fr' ? 'fr' : code.toLowerCase()
                                        )
                                    }}
                                </span>
                            </a>
                        </p>
                        <!-- English version -->
                        <template v-if="siteCountryCode && siteCountryCode !== 'CH' && !['DE', 'AT'].includes(code)">
                            <p>
                                {{ $t('pages.insurance.english_version_below', 'en') }}
                            </p>
                            <p>
                                <a :href="getInsuranceTermsUrl(code, true)" target="_blank">
                                    <svg-icon name="download" />
                                    <span>{{ $t('pages.insurance.terms_link', 'en') }}</span>
                                </a>
                            </p>
                            <p>
                                <a :href="getCancellationInsuranceTermsUrl(code, true)" target="_blank">
                                    <svg-icon name="download" />
                                    <span>{{ $t('pages.insurance.cancellation_terms_link', 'en') }}</span>
                                </a>
                            </p>
                            <p>
                                <a :href="getInsuranceProductInfoUrl(code, true)" target="_blank">
                                    <svg-icon name="download" />
                                    <span>{{ $t('pages.insurance.product_info_link', 'en') }}</span>
                                </a>
                            </p>
                        </template>
                    </TextExpander>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.how_to_subheading') }}
                    </h3>
                    <p>
                        {{ $t('pages.insurance.how_to_desc1') }}
                    </p>
                    <p>
                        {{ $t('pages.insurance.how_to_desc2') }}
                    </p>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.coverage_heading') }}
                    </h3>
                    <p>
                        {{ $t('pages.insurance.coverage_desc') }}
                    </p>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.traffic_insurance_subheading') }}
                    </h3>
                    <p>
                        {{ $t('pages.insurance.traffic_insurance_desc') }}
                    </p>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.basic_insurance_subheading') }}
                    </h3>
                    <p>
                        {{ $t('pages.insurance.basic_insurance_desc1') }}
                    </p>
                    <p>
                        {{ $t('pages.insurance.basic_insurance_desc2') }}
                    </p>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.damage_examples_heading') }}
                    </h3>
                    <ul class="icon-list icon-list--included">
                        <li>
                            <svg-icon name="check_circle" />
                            <span>{{ $t('pages.insurance.damage_examples_1') }}</span>
                        </li>
                        <li>
                            <svg-icon name="check_circle" />
                            <span>{{ $t('pages.insurance.damage_examples_2') }}</span>
                        </li>
                        <li>
                            <svg-icon name="check_circle" />
                            <span>{{ $t('pages.insurance.damage_examples_3') }}</span>
                        </li>
                        <li>
                            <svg-icon name="check_circle" />
                            <span>{{ $t('pages.insurance.damage_examples_4') }}</span>
                        </li>
                        <li>
                            <svg-icon name="check_circle" />
                            <span>{{ $t('pages.insurance.damage_examples_5') }}</span>
                        </li>
                    </ul>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.not_included_examples_subheading') }}
                    </h3>
                    <ul class="icon-list icon-list--excluded">
                        <li>
                            <svg-icon name="warning" />
                            <span>{{ $t('pages.insurance.not_included_examples_1') }}</span>
                        </li>
                        <li>
                            <svg-icon name="warning" />
                            <span>
                                {{ $t('pages.insurance.not_included_examples_2') }}
                            </span>
                        </li>
                        <li>
                            <svg-icon name="warning" />
                            <span>
                                {{ $t('pages.insurance.not_included_examples_3') }}
                            </span>
                        </li>
                    </ul>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.why_misfortune_title') }}
                    </h3>
                    <p>{{ $t('pages.insurance.why_misfortune_text') }}</p>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.misfortune_damage_examples_title') }}
                    </h3>
                    <ol class="insurance__ordered-list">
                        <i18n path="modals.opt_for_omocom_interior_insurance.more_insurance_info.item1" tag="li">
                            <template #subtitle>
                                <strong>{{
                                    $t('modals.opt_for_omocom_interior_insurance.more_insurance_info.item1_subtitle')
                                }}</strong>
                            </template>
                        </i18n>

                        <i18n path="modals.opt_for_omocom_interior_insurance.more_insurance_info.item2" tag="li">
                            <template #subtitle>
                                <strong>{{
                                    $t('modals.opt_for_omocom_interior_insurance.more_insurance_info.item2_subtitle')
                                }}</strong>
                            </template>
                        </i18n>

                        <i18n path="modals.opt_for_omocom_interior_insurance.more_insurance_info.item3" tag="li">
                            <template #subtitle>
                                <strong>{{
                                    $t('modals.opt_for_omocom_interior_insurance.more_insurance_info.item3_subtitle')
                                }}</strong>
                            </template>
                        </i18n>

                        <i18n path="modals.opt_for_omocom_interior_insurance.more_insurance_info.item4" tag="li">
                            <template #subtitle>
                                <strong>{{
                                    $t('modals.opt_for_omocom_interior_insurance.more_insurance_info.item4_subtitle')
                                }}</strong>
                            </template>
                        </i18n>
                    </ol>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.about_omocom_subheading') }}
                    </h3>
                    <p>
                        {{ $t('pages.insurance.about_omocom_desc1') }}
                    </p>
                    <i18n path="pages.insurance.about_omocom_desc2" tag="p">
                        <template #link>
                            <a :href="omocomClaimsFormUrl.full" target="_blank">{{ omocomClaimsFormUrl.display }}</a>
                        </template>
                    </i18n>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.reimbursement_subheading') }}
                    </h3>
                    <p>
                        {{ $t('pages.insurance.reimbursement_desc1') }}
                    </p>
                    <p>
                        {{ $t('pages.insurance.reimbursement_desc2') }}
                    </p>
                </div>
                <div class="section__part">
                    <h3 class="section__subheading">
                        {{ $t('pages.insurance.contact_subheading') }}
                    </h3>
                    <i18n path="pages.insurance.contact_desc" tag="p">
                        <template #link>
                            <a :href="omocomClaimsFormUrl.full" target="_blank">{{ omocomClaimsFormUrl.display }}</a>
                        </template>
                    </i18n>
                    <p>
                        {{ $t('pages.insurance.contact_desc_2') }}
                    </p>
                    <ul class="icon-list">
                        <li class="icon-list__phone">
                            <svg-icon name="phone" />
                            <a :href="'tel:' + omocomPhoneNumberNormalized">{{ omocomPhoneNumber }}</a>
                        </li>
                        <li>
                            <svg-icon name="mail" />
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                            <a href="mailto:hello@omocom.insurance">hello@omocom.insurance</a>
                        </li>
                    </ul>
                    <p>
                        {{ $t('pages.insurance.contact_desc_3') }}
                    </p>
                </div>

                <div class="section__cta-buttons">
                    <LocalizedLink :to="{ name: 'rent-out' }" class="button button--secondary-contrast">
                        {{ $t('pages.insurance.rent_out_btn_label') }}
                    </LocalizedLink>
                    <LocalizedLink
                        :to="
                            localePath({
                                path: `/${$t('common.motorhomes_page_path')}`,
                                query: { [$t('common.all_vehicles_query')]: 1 },
                            })
                        "
                        class="button button--primary"
                    >
                        {{ $t('pages.insurance.vehicles_btn_label') }}
                    </LocalizedLink>
                </div>
            </div>
            <Contact />
        </section>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import TextExpander from '~/components/TextExpander.vue';
import Contact from '~/components/sections/Contact.vue';
import Notice from '~/components/Notice.vue';
import { omocomMarketCountryCodes } from '~/config/countryCodeLists';

export default {
    name: 'Omocom',
    components: {
        TextExpander,
        Contact,
        Notice,
    },
    head() {
        return {
            title: this.$t('pages.insurance.page_title_omocom'),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.$t('pages.insurance.page_description_omocom'),
                },
            ],
        };
    },
    nuxtI18n: {
        paths: {
            se: '/forsakring/omocom',
            'fi-sv': '/forsakring/omocom',
            en: '/insurance/omocom',
            de: '/versicherung/omocom',
            at: '/versicherung/omocom',
            fi: '/vakuutus/omocom',
            no: '/forsikring/omocom',
            dk: '/forsikring/omocom',
            ch: '/versicherung/omocom',
            fr: '/assurance/omocom',
        },
    },
    computed: {
        ...mapGetters(['siteCountryCode']),
        omocomClaimsFormUrl() {
            return {
                full: 'https://external.omocom.se/claimsform',
                display: 'external.omocom.se/claimsform',
            };
        },
        omocomPhoneNumber() {
            switch (this.siteCountryCode) {
                case 'NO':
                case 'FI':
                case 'DK':
                case 'SE':
                default:
                    return '+46 8-520 278 70';
            }
        },
        omocomPhoneNumberNormalized() {
            switch (this.siteCountryCode) {
                case 'NO':
                case 'FI':
                case 'DK':
                case 'SE':
                default:
                    return '+46852027870';
            }
        },
        regionCodes() {
            let regionCodes = omocomMarketCountryCodes;

            if (this.siteCountryCode && this.siteCountryCode !== 'CH') {
                regionCodes = regionCodes.filter(code => code !== this.siteCountryCode);
                regionCodes.unshift(this.siteCountryCode);
            }

            return regionCodes;
        },
    },
    methods: {
        getRoadsideAssistancePhone(code) {
            switch (code || this.siteCountryCode) {
                case 'FI':
                    return '+358 9 3747 7431';
                case 'NO':
                    return '(0047) 242 401 86';
                case 'DK':
                    return '+45 70 202 908';
                case 'SE':
                default:
                    return '+46 8 731 40 68';
            }
        },
        getInsuranceProductInfoUrl(countryCode, english) {
            const baseUrl = 'https://www.omocom.insurance/MyCamper/IPID';

            if (english && !['DE', 'AT'].includes(countryCode)) {
                return `${baseUrl}/${countryCode}/ENG`;
            }

            switch (countryCode) {
                case 'FI':
                    return `${baseUrl}/FI/FI`;

                case 'NO':
                    return `${baseUrl}/NO/NO`;

                case 'DK':
                    return `${baseUrl}/DK/DK`;

                case 'DE':
                case 'AT':
                    return `${baseUrl}/DE`;

                case 'SE':
                default:
                    return `${baseUrl}/SE/SE`;
            }
        },
        getInsuranceTermsUrl(countryCode, english) {
            const baseUrl = 'https://www.omocom.insurance/MyCamper/Terms/Rental';

            if (english) {
                return `${baseUrl}/${countryCode}/EN`;
            }

            switch (countryCode) {
                case 'FI':
                    return `${baseUrl}/FI/FI`;

                case 'NO':
                    return `${baseUrl}/NO/NB`;

                case 'DK':
                    return `${baseUrl}/DK/DA`;

                case 'SE':
                default:
                    return `${baseUrl}/SE/SV`;
            }
        },
        getCancellationInsuranceTermsUrl(countryCode, english) {
            const baseUrl = 'https://www.omocom.insurance/MyCamper/Terms/Cancellation';

            if (english) {
                return `${baseUrl}/${countryCode}/ENG`;
            }

            switch (countryCode) {
                case 'FI':
                    return `${baseUrl}/FI/FI`;

                case 'NO':
                    return `${baseUrl}/NO/NB`;

                case 'DK':
                    return `${baseUrl}/DK/DA`;

                case 'SE':
                default:
                    return `${baseUrl}/SE/SV`;
            }
        },
        regionExpanderLabel(code) {
            if (!this.siteCountryCode) {
                return this.$t(`pages.insurance.renting_in_${code}`);
            }

            return this.$t(`pages.insurance.renting_in_${code}`, code.toLowerCase());
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/blocks/_insurance.scss';
</style>
